import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Check } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { HUBS } from "@/lib/brand";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { routeForUser } from "@/lib/nav";

const DISCOVER = ["Member referral", "Event", "Social media", "Partner venue", "Press", "Other"];

export default function Apply() {
  const { user, register, t, formatError } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", surname: "", date_of_birth: "", email: "", phone: "", country: "", city: "",
    preferred_city: HUBS[0], how_discovered: DISCOVER[0], introduction: "", password: "",
    accept_terms: false, accept_privacy: false, confirm_adult: false,
  });
  const [photoPath, setPhotoPath] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate(routeForUser(user), { replace: true }); }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
      setPhotoPreview(URL.createObjectURL(file));
      toast.success(t("upload_photo"));
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.accept_terms || !form.accept_privacy || !form.confirm_adult) { setError(t("accept_terms")); return; }
    setLoading(true);
    try {
      const u = await register({ ...form, profile_photo_path: photoPath });
      toast.success(t("submit_application"));
      navigate(routeForUser(u), { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const inputCls = "cu-input w-full px-4 py-3 text-sm";

  return (
    <div className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-6 pt-6">
        <Link to="/" className="eyebrow hover:text-[color:var(--cu-gold-light)]">← Caught Up</Link>
        <LangToggle />
      </header>
      <div className="flex-1 px-6 py-8 max-w-lg mx-auto w-full fade-up">
        <Logo size="md" />
        <h1 className="font-serif-cu text-2xl text-center mt-8 mb-2 text-[color:var(--cu-text)]">{t("application")}</h1>
        <p className="text-xs text-center text-[color:var(--cu-muted)] mb-8 leading-relaxed">{t("adults_only")}</p>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">{t("first_name")}</label>
              <input data-testid="apply-first_name-input" required value={form.first_name} onChange={set("first_name")} className={inputCls} />
            </div>
            <div>
              <label className="eyebrow block mb-2">{t("surname")}</label>
              <input data-testid="apply-surname-input" required value={form.surname} onChange={set("surname")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("date_of_birth")}</label>
            <input data-testid="apply-date_of_birth-input" type="date" required value={form.date_of_birth} onChange={set("date_of_birth")} className={inputCls} />
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("email")}</label>
            <input data-testid="apply-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} autoComplete="email" />
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("phone")}</label>
            <input data-testid="apply-phone-input" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">{t("country")}</label>
              <input data-testid="apply-country-input" required value={form.country} onChange={set("country")} className={inputCls} />
            </div>
            <div>
              <label className="eyebrow block mb-2">{t("city")}</label>
              <input data-testid="apply-city-input" required value={form.city} onChange={set("city")} className={inputCls} />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-2">{t("profile_photo")}</label>
            <label className="cu-input w-full px-4 py-3 text-sm flex items-center gap-3 cursor-pointer" data-testid="apply-photo-upload">
              {photoPreview ? <img src={photoPreview} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <Upload size={18} className="text-[color:var(--cu-gold)]" />}
              <span className="text-[color:var(--cu-muted)]">{uploading ? "…" : photoPath ? t("change_photo") : t("upload_photo")}</span>
              {photoPath && <Check size={16} className="ml-auto text-emerald-400" />}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
            </label>
          </div>

          <div>
            <label className="eyebrow block mb-2">{t("preferred_city")}</label>
            <select data-testid="apply-preferred-city-select" value={form.preferred_city} onChange={set("preferred_city")} className={inputCls}>
              {HUBS.map((h) => <option key={h} value={h} className="bg-[color:var(--cu-charcoal)]">{h}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("how_discovered")}</label>
            <select data-testid="apply-how-discovered-select" value={form.how_discovered} onChange={set("how_discovered")} className={inputCls}>
              {DISCOVER.map((d) => <option key={d} value={d} className="bg-[color:var(--cu-charcoal)]">{d}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("introduction")}</label>
            <textarea data-testid="apply-introduction-input" value={form.introduction} onChange={set("introduction")} rows={3} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <label className="eyebrow block mb-2">{t("password")}</label>
            <input data-testid="apply-password-input" type="password" required minLength={8} value={form.password} onChange={set("password")} className={inputCls} autoComplete="new-password" />
          </div>

          <div className="space-y-3 pt-2">
            {[["accept_terms", "accept_terms"], ["accept_privacy", "accept_privacy"], ["confirm_adult", "confirm_adult"]].map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 cursor-pointer">
                <input data-testid={`apply-${k.replace(/_/g, "-")}-checkbox`} type="checkbox" checked={form[k]} onChange={set(k)} className="mt-0.5 accent-[color:var(--cu-gold)] w-4 h-4" />
                <span className="text-xs text-[color:var(--cu-muted)] leading-relaxed">{t(label)}</span>
              </label>
            ))}
          </div>

          {error && <p data-testid="apply-error" className="text-xs text-red-400">{error}</p>}
          <button data-testid="membership-form-submit-button" disabled={loading} className="btn-gold w-full py-3.5 text-sm">{loading ? t("submitting") : t("submit_application")}</button>
        </form>

        <p className="text-center mt-6 mb-10">
          <Link to="/login" className="eyebrow hover:text-[color:var(--cu-gold-light)]">{t("already_member")} · {t("sign_in")}</Link>
        </p>
      </div>
    </div>
  );
}
